import React, { useState } from 'react';
import {
  CallingCodeOption,
  PhoneNumber,
  PhoneNumberInput as BasePhoneNumberInput,
} from 'glints-aries';
import { defineMessages, useIntl } from 'react-intl';

import { addSpaceEveryXCharacters } from '../../common/helpers/string';
import { useCountryCallingCodeOptions } from '../../common/hooks/useCountryCallingCodeOptions';
const CHUNK_SIZE = 4;

export const PhoneNumberInput = ({
  onChange,
  value,
  className,
  error,
  onFocus,
  onBlur,
  addon,
  isWhatsAppInput = false,
}: Props) => {
  const { formatMessage } = useIntl();

  const [filterInput, setFilterInput] = useState<string>('');

  const { callingCodeOptions, countryCodeOptionsAreLoading } =
    useCountryCallingCodeOptions({ filterInput, isWhatsAppInput });
  const optionsWithoutCountryMetadata: CallingCodeOption[] =
    callingCodeOptions.map(option => ({
      callingCode: Number(option.callingCode.replace(/\D/g, '')),
      label: option.label,
      isFeatured: option.isFeatured,
    }));

  return (
    <BasePhoneNumberInput
      addon={addon}
      value={{
        ...value,
        significantNumber:
          addSpaceEveryXCharacters(
            value?.significantNumber ?? '',
            CHUNK_SIZE
          ) || '',
      }}
      callingCodeOptions={optionsWithoutCountryMetadata}
      filterValue={filterInput}
      isLoadingCallingCodeOptions={countryCodeOptionsAreLoading}
      label={formatMessage(messages.label)}
      featuredOptionsLabel={
        isWhatsAppInput ? '' : formatMessage(messages.featuredOptionsLabel)
      }
      otherOptionsLabel={formatMessage(messages.otherOptionsLabel)}
      callingCodeFilterInputPlaceholder={formatMessage(
        messages.callingCodeFilterInputPlaceholder
      )}
      callingCodeNoOptionsLabel={formatMessage(
        messages.callingCodeNoOptionsLabel
      )}
      error={error}
      onChange={value =>
        onChange({
          callingCode: value.callingCode,
          significantNumber:
            value.significantNumber?.replace(/[^0-9]/g, '') ?? '',
        })
      }
      onInputChange={filterInput => setFilterInput(filterInput || '')}
      onFocus={onFocus}
      onBlur={onBlur}
      {...{ className }}
    />
  );
};

interface Props {
  className: string;
  onChange: (value: PhoneNumber) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  value: PhoneNumber;
  error?: string;
  addon?: React.ReactElement;
  isWhatsAppInput?: boolean;
}

export const PHONE_NUMBER_SEPARATOR = ' ';

export const stringifyPhoneNumber = ({
  callingCode,
  significantNumber,
}: PhoneNumber) =>
  ['+', callingCode, PHONE_NUMBER_SEPARATOR, significantNumber].join('');

const messages = defineMessages({
  label: { id: 'phoneNumberInput.label', defaultMessage: 'Mobile Number' },
  featuredOptionsLabel: {
    id: 'phoneNumberInput.featuredOptionsLabel',
    defaultMessage: 'Frequently Used',
  },
  otherOptionsLabel: {
    id: 'phoneNumberInput.otherOptionsLabel',
    defaultMessage: 'The Rest Of The World',
  },
  callingCodeFilterInputPlaceholder: {
    id: 'phoneNumberInput.callingCodeFilterInputPlaceholder',
    defaultMessage: 'Type country code or country name',
  },
  callingCodeNoOptionsLabel: {
    id: 'text-no-results-found',
    defaultMessage: 'No results found',
  },
  currentCountry: {
    id: 'phoneNumberInput.currentCountry',
    defaultMessage: 'Current Country',
  },
});
